import { useTranslation } from "react-i18next";
import { CodeDescriptionDto, PreprocessingFileDto } from "api/api.typing";
import BatchMultiSelect from "components/Dropdown/BatchMultiSelect";
import { FileMapping } from "./useFileMappingState";

interface FileMappingGridRowProps {
  file: File;
  mapping: FileMapping;
  isSelected: boolean;
  sourceFiles: PreprocessingFileDto[];
  targetLanguages: CodeDescriptionDto[];
  onSelect: (fileName: string, checked: boolean) => void;
  onSourceFileChange: (fileName: string, sourceFileIds: number[]) => void;
  onLanguageChange: (fileName: string, languageCodes: string[]) => void;
}

export function FileMappingGridRow({
  file,
  mapping,
  isSelected,
  sourceFiles,
  targetLanguages,
  onSelect,
  onSourceFileChange,
  onLanguageChange,
}: FileMappingGridRowProps) {
  const { t: translation } = useTranslation();

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
      {/* Checkbox */}
      <td className="px-3 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(file.name, e.target.checked)}
          className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>

      {/* Uploaded file name */}
      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
        <div className="max-w-[250px] truncate" title={file.name}>
          {file.name}
        </div>
      </td>

      {/* Source files multi-select */}
      <td className="w-[500px] max-w-[500px] px-6 py-4">
        <BatchMultiSelect<number>
          value={mapping.sourceFileIds ?? []}
          onChange={(ids) => onSourceFileChange(file.name, ids)}
          items={sourceFiles.map((f) => ({ id: f.id, name: f.fileName }))}
          placeholder={translation("task.upload.file-mapping.source-files")}
          withNone={false}
        />
      </td>

      {/* Languages multi-select */}
      <td className="w-[350px] px-6 py-4">
        <BatchMultiSelect<string>
          value={mapping.languageCodes}
          onChange={(codes) => onLanguageChange(file.name, codes)}
          items={targetLanguages.map((l) => ({ id: l.code, name: l.description }))}
          placeholder={translation("task.upload.file-mapping.languages")}
          withNone={false}
        />
      </td>
    </tr>
  );
}
